import React, { useContext, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import IframeCodeControl from '../../ws-iframe/components/iframe-code-control';
import IframePreview from '../../ws-iframe/components/iframe-preview';
import IframeUrlControl from '../../ws-iframe/components/iframe-url-control';
import iframeEmbeddingTypes from '../../ws-iframe/constants/iframe-embedding-types';
import createSubmitTraitsIframeHandler from '../../ws-iframe/helpers/create-submit-traits-iframe-handler';
import getIframeDefaultFieldValues from '../../ws-iframe/helpers/get-iframe-default-field-values';
import chunkIds from '../constants/chunk-ids';
import mediaTypeIds from '../constants/media-type-ids';
import { iframeFieldNames } from '../constants/field-names';
import createSubmitActionsIframeHandler from '../helpers/create-submit-actions-iframe-handler';
import { getIframeFieldsValues } from '../helpers/get-fields-values';
import mediaContainerBuilder from '../helpers/media-container-builder';

const { Box, Stack, HorizontalStack, SimpleSelectField, Heading, ToggleSection } = designSystem;
const { useWatch } = reactHookForm;
const {
  Context: ElementSettingsContext,
  constants: { stages },
  hooks: { useMountChunk },
} = elementSettings;

const ALLOW_FULLSCREEN_CONTROL_ID = 'allow-full-screen';

const getTypeOptions = () => [
  {
    value: iframeEmbeddingTypes.URL,
    label: i18next.t('URL'),
  },
  {
    value: iframeEmbeddingTypes.CODE,
    label: i18next.t('Code'),
  },
];

const embeddingTypeToValidatedFieldName = {
  [iframeEmbeddingTypes.URL]: iframeFieldNames.IFRAME_URL,
  [iframeEmbeddingTypes.CODE]: iframeFieldNames.IFRAME_CODE,
};

const getDefaultValue = ({ wsIframeElement, cachedFieldsValues }) => {
  const defaultValuesFromElement = getIframeDefaultFieldValues(wsIframeElement);

  const embeddingType =
    cachedFieldsValues?.[iframeFieldNames.IFRAME_EMBEDDING_TYPE] ??
    defaultValuesFromElement.embeddingType;
  const url =
    cachedFieldsValues?.[iframeFieldNames.IFRAME_URL] ??
    defaultValuesFromElement[iframeFieldNames.IFRAME_URL];
  const allowFullscreen =
    cachedFieldsValues?.[iframeFieldNames.IFRAME_ALLOW_FULLSCREEN] ??
    defaultValuesFromElement[iframeFieldNames.IFRAME_ALLOW_FULLSCREEN];
  const code =
    cachedFieldsValues?.[iframeFieldNames.IFRAME_CODE] ??
    defaultValuesFromElement[iframeFieldNames.IFRAME_CODE];

  return {
    [iframeFieldNames.IFRAME_EMBEDDING_TYPE]: embeddingType,
    [iframeFieldNames.IFRAME_URL]: url,
    [iframeFieldNames.IFRAME_ALLOW_FULLSCREEN]: allowFullscreen,
    [iframeFieldNames.IFRAME_CODE]: code,
  };
};

function IframeSettingsContainer({ mediaContainerElement, tabId, settingId, cachedFieldsValues }) {
  const {
    dialog,
    tabs,
    form: { control, triggerFieldValidation, getValues },
    callbacks,
  } = useContext(ElementSettingsContext);

  const wsIframeElement = mediaContainerElement.querySelector('ws-iframe');
  const initialUrl = wsIframeElement?.getAttribute('src') || '';
  const chunkId = chunkIds.IFRAME;
  const isActiveTab = tabs.activeId === tabId;

  const typeOptions = useMemo(() => getTypeOptions(), []);
  // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  const defaultValues = useMemo(() => getDefaultValue({ wsIframeElement, cachedFieldsValues }), []);

  const iframeElementRef = useRef(mediaContainerElement.querySelector('iframe'));

  const [previewUrl, setPreviewUrl] = useState(initialUrl);

  const iframeType = useWatch({
    control,
    name: iframeFieldNames.IFRAME_EMBEDDING_TYPE,
  });

  const getFieldsValues = useCallback(() => getIframeFieldsValues(getValues), [getValues]);

  const updatePreviewUrl = useCallback((newUrl) => {
    setPreviewUrl(String(newUrl || ''));
  }, []);

  const handleUpdate = useCallback(
    async (iframeEmbeddingType) => {
      const isValid = await triggerFieldValidation(
        embeddingTypeToValidatedFieldName[iframeEmbeddingType]
      );

      if (isValid) {
        const fieldsValues = getFieldsValues();
        const iframeElement = mediaContainerBuilder.replaceContentFromFieldsValues(
          mediaContainerElement,
          mediaTypeIds.VIDEO,
          fieldsValues
        );

        iframeElementRef.current = mediaContainerElement.querySelector('iframe');
        updatePreviewUrl(iframeElement.getAttribute('src'));
      } else {
        updatePreviewUrl('');
      }
    },
    [triggerFieldValidation, getFieldsValues, mediaContainerElement, updatePreviewUrl]
  );

  const handleTypeChange = useCallback(
    (option) => {
      const iframeEmbeddingType = option?.value;

      handleUpdate(iframeEmbeddingType);
      callbacks.onControlClick({
        settingId,
        controlId: `embed-via-${iframeEmbeddingType}`,
      });
    },
    [callbacks, settingId, handleUpdate]
  );

  const handleIframeUrlChange = useCallback(() => {
    handleUpdate(iframeEmbeddingTypes.URL);
  }, [handleUpdate]);

  const handleAllowFullscreenChange = useCallback(
    (value) => {
      callbacks.onToggleClick({
        settingId,
        controlId: ALLOW_FULLSCREEN_CONTROL_ID,
        isActive: value,
      });
    },
    [callbacks, settingId]
  );

  const handleIframeCodeChange = useCallback(() => {
    handleUpdate(iframeEmbeddingTypes.CODE);
  }, [handleUpdate]);

  const actionsHandler = useMemo(
    () =>
      createSubmitActionsIframeHandler({
        mediaContainerElement,
      }),
    [mediaContainerElement]
  );

  const traitsHandler = useMemo(
    () =>
      createSubmitTraitsIframeHandler({
        prefix: settingId,
      }),
    [settingId]
  );

  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    defaultValues,
    stageHandlers: [
      {
        stage: stages.SUBMIT_ACTIONS_GENERATION,
        handler: actionsHandler,
      },
      {
        stage: stages.SUBMIT_TRAITS_GENERATION,
        handler: traitsHandler,
      },
    ],
    withUnmount: true,
  });

  if (!isChunkMounted) return null;

  return (
    <>
      <Box paddingLeft="spacing-m" paddingRight="spacing-m" paddingBottom="spacing-xs">
        <IframePreview
          key={isActiveTab}
          url={previewUrl}
          mediaElement={mediaContainerElement}
          iframeElement={iframeElementRef.current}
        />
      </Box>
      <Box paddingLeft="spacing-m" paddingRight="spacing-m">
        <Stack gap="spacing-2-xs">
          <HorizontalStack verticalAlign="center" align="space-between" marginBottom="spacing-s">
            <HorizontalStack verticalAlign="center">
              <Heading type="heading-6" singleLine>
                {i18next.t('Embed via')}
              </Heading>
            </HorizontalStack>
            <SimpleSelectField
              name={iframeFieldNames.IFRAME_EMBEDDING_TYPE}
              control={control}
              options={typeOptions}
              menuWidth="auto"
              transparent
              noVerticalSpacing
              noHorizontalSpacing
              noSpacingBetweenElements
              onChange={handleTypeChange}
            />
          </HorizontalStack>

          {iframeType === iframeEmbeddingTypes.URL && (
            <>
              <IframeUrlControl control={control} onChange={handleIframeUrlChange} />
              <ToggleSection
                name={iframeFieldNames.IFRAME_ALLOW_FULLSCREEN}
                control={control}
                title={i18next.t('Allow full screen')}
                onChange={handleAllowFullscreenChange}
              />
            </>
          )}

          {iframeType === iframeEmbeddingTypes.CODE && (
            <IframeCodeControl control={control} onChange={handleIframeCodeChange} />
          )}
        </Stack>
      </Box>

      <style
        // eslint-disable-next-line yola/react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
            .ace_tooltip {
              max-width: initial;
              transform: translate(${-dialog.position.x}px, ${-dialog.position.y}px);
            }
          `,
        }}
      />
    </>
  );
}

IframeSettingsContainer.propTypes = {
  mediaContainerElement: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.object])
    .isRequired,
  tabId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
  cachedFieldsValues: PropTypes.object.isRequired,
};

export default IframeSettingsContainer;
